import { registerHandler } from '@/modules/events/eventBus.service';
import type {
  CommercialOperationEventPayload,
  InvoiceEventPayload,
  LogisticsScheduledPayload,
  InventoryAdjustmentPayload,
  InventoryDiscrepancyPayload,
  InventoryMovementEventPayload,
  MaintenanceConflictPayload,
  MaintenanceEventPayload,
  PermissionDeniedPayload,
  ReservationConflictPayload,
  ReservationEventPayload,
} from '@/modules/events/types';
import { createNotification } from '@/services/notifications';

function recipientIds(actorUid: string): string[] {
  return [actorUid];
}

async function notifyUser(
  userId: string,
  input: Omit<Parameters<typeof createNotification>[0], 'userId'>,
): Promise<void> {
  await createNotification({ ...input, userId });
}

async function handleReservationCreated(
  payload: ReservationEventPayload,
): Promise<void> {
  for (const userId of recipientIds(payload.actorUid)) {
    await notifyUser(userId, {
      type: 'reservation_created',
      title: 'Reserva creada',
      message: `«${payload.title}» (${payload.startDate} → ${payload.endDate})`,
      priority: 'medium',
      entityType: 'reservation',
      entityId: payload.reservationId,
      metadata: { status: payload.status },
    });
  }
}

async function handleReservationCancelled(
  payload: ReservationEventPayload,
): Promise<void> {
  for (const userId of recipientIds(payload.actorUid)) {
    await notifyUser(userId, {
      type: 'reservation_cancelled',
      title: 'Reserva cancelada',
      message: `«${payload.title}» fue cancelada.`,
      priority: 'medium',
      entityType: 'reservation',
      entityId: payload.reservationId,
    });
  }
}

async function handleReservationConflict(
  payload: ReservationConflictPayload,
): Promise<void> {
  for (const userId of recipientIds(payload.actorUid)) {
    await notifyUser(userId, {
      type: 'reservation_conflict',
      title: 'Conflicto de disponibilidad',
      message:
        payload.conflictCount > 0
          ? `Hay ${payload.conflictCount} conflicto(s) en el rango seleccionado.`
          : 'No hay disponibilidad para los artículos o fechas elegidos.',
      priority: 'high',
      entityType: 'reservation',
      ...(payload.reservationId ? { entityId: payload.reservationId } : {}),
      metadata: {
        conflictCount: payload.conflictCount,
        ...(payload.startDate ? { startDate: payload.startDate } : {}),
        ...(payload.endDate ? { endDate: payload.endDate } : {}),
      },
    });
  }
}

async function handleCommercialOperationCreated(
  payload: CommercialOperationEventPayload,
): Promise<void> {
  for (const userId of recipientIds(payload.actorUid)) {
    await notifyUser(userId, {
      type: 'commercial_operation_created',
      title: 'Operación comercial',
      message: `«${payload.title}» registrada (${payload.status}).`,
      priority: 'low',
      entityType: 'commercialOperation',
      entityId: payload.operationId,
    });
  }
}

async function handleLogisticsScheduled(
  payload: LogisticsScheduledPayload,
): Promise<void> {
  for (const userId of recipientIds(payload.actorUid)) {
    await notifyUser(userId, {
      type: 'logistics_scheduled',
      title: 'Logística programada',
      message: `Orden programada para ${payload.scheduledDate}.`,
      priority: 'medium',
      entityType: 'logisticsOrder',
      entityId: payload.orderId,
      metadata: { type: payload.type },
    });
  }
}

async function handleInvoiceCreated(
  payload: InvoiceEventPayload,
): Promise<void> {
  for (const userId of recipientIds(payload.actorUid)) {
    await notifyUser(userId, {
      type: 'invoice_created',
      title: 'Factura creada',
      message: `Factura ${payload.invoiceId.slice(0, 8)}… — ${payload.total} ${payload.currency}`,
      priority: 'medium',
      entityType: 'invoice',
      entityId: payload.invoiceId,
      metadata: { status: payload.status },
    });
  }
}

async function handleInvoicePending(
  payload: InvoiceEventPayload,
): Promise<void> {
  for (const userId of recipientIds(payload.actorUid)) {
    await notifyUser(userId, {
      type: 'invoice_pending',
      title: 'Factura pendiente',
      message: `La factura requiere seguimiento (${payload.total} ${payload.currency}).`,
      priority: 'high',
      entityType: 'invoice',
      entityId: payload.invoiceId,
    });
  }
}

async function handleMaintenanceScheduled(
  payload: MaintenanceEventPayload,
): Promise<void> {
  for (const userId of recipientIds(payload.actorUid)) {
    await notifyUser(userId, {
      type: 'maintenance_scheduled',
      title: 'Mantenimiento programado',
      message: `«${payload.title}» (${payload.scheduledStart ?? ''} → ${payload.scheduledEnd ?? ''})`,
      priority: 'medium',
      entityType: 'maintenanceTask',
      entityId: payload.taskId,
      metadata: { type: payload.type, inventoryItemId: payload.inventoryItemId },
    });
  }
}

async function handleMaintenanceStarted(
  payload: MaintenanceEventPayload,
): Promise<void> {
  for (const userId of recipientIds(payload.actorUid)) {
    await notifyUser(userId, {
      type: 'maintenance_started',
      title: 'Reparación / mantenimiento iniciado',
      message: `«${payload.title}» en curso.`,
      priority: 'high',
      entityType: 'maintenanceTask',
      entityId: payload.taskId,
    });
  }
}

async function handleMaintenanceCompleted(
  payload: MaintenanceEventPayload,
): Promise<void> {
  for (const userId of recipientIds(payload.actorUid)) {
    await notifyUser(userId, {
      type: 'maintenance_completed',
      title: 'Mantenimiento completado',
      message: `«${payload.title}» finalizado.`,
      priority: 'medium',
      entityType: 'maintenanceTask',
      entityId: payload.taskId,
    });
  }
}

async function handleMaintenanceConflict(
  payload: MaintenanceConflictPayload,
): Promise<void> {
  for (const userId of recipientIds(payload.actorUid)) {
    await notifyUser(userId, {
      type: 'maintenance_conflict',
      title: 'Conflicto mantenimiento / reserva',
      message: `Solapamiento en ${payload.scheduledStart} → ${payload.scheduledEnd} para el artículo.`,
      priority: 'high',
      entityType: 'maintenanceTask',
      ...(payload.taskId ? { entityId: payload.taskId } : {}),
      metadata: { inventoryItemId: payload.inventoryItemId },
    });
  }
}

async function handleInventoryTransfer(
  payload: InventoryMovementEventPayload,
): Promise<void> {
  for (const userId of recipientIds(payload.actorUid)) {
    await notifyUser(userId, {
      type: 'inventory_transfer',
      title: 'Transferencia de inventario',
      message: `Artículo movido entre ubicaciones.`,
      priority: 'medium',
      entityType: 'inventoryMovement',
      entityId: payload.movementId,
      metadata: { inventoryItemId: payload.inventoryItemId },
    });
  }
}

async function handleInventoryAdjustment(
  payload: InventoryAdjustmentPayload,
): Promise<void> {
  for (const userId of recipientIds(payload.actorUid)) {
    await notifyUser(userId, {
      type: 'inventory_adjustment',
      title: 'Ajuste manual de inventario',
      message: `Cantidad ajustada: ${payload.quantity}`,
      priority: 'high',
      entityType: 'inventoryMovement',
      entityId: payload.movementId,
    });
  }
}

async function handleInventoryDiscrepancy(
  payload: InventoryDiscrepancyPayload,
): Promise<void> {
  for (const userId of recipientIds(payload.actorUid)) {
    await notifyUser(userId, {
      type: 'inventory_discrepancy',
      title: 'Discrepancia en conteo',
      message: payload.notes,
      priority: 'high',
      entityType: 'inventoryMovement',
      entityId: payload.movementId,
    });
  }
}

async function handleInventoryMovementCritical(
  payload: InventoryMovementEventPayload,
): Promise<void> {
  for (const userId of recipientIds(payload.actorUid)) {
    await notifyUser(userId, {
      type: 'inventory_movement_critical',
      title: 'Movimiento crítico',
      message: `Movimiento ${payload.type ?? ''} registrado.`,
      priority: 'high',
      entityType: 'inventoryMovement',
      entityId: payload.movementId,
    });
  }
}

async function handlePermissionDenied(
  payload: PermissionDeniedPayload,
): Promise<void> {
  for (const userId of recipientIds(payload.actorUid)) {
    await notifyUser(userId, {
      type: 'permission_denied',
      title: 'Acceso no permitido',
      message: 'Intentaste acceder a una sección sin permisos suficientes.',
      priority: 'low',
      entityType: 'system',
      entityId: payload.permission,
      metadata: { path: payload.path },
    });
  }
}

let registered = false;

/** Registra handlers de notificaciones (idempotente). */
export function registerNotificationEventHandlers(): void {
  if (registered) return;
  registered = true;

  registerHandler('reservation.created', handleReservationCreated);
  registerHandler('reservation.cancelled', handleReservationCancelled);
  registerHandler('reservation.conflict', handleReservationConflict);
  registerHandler(
    'commercial.operation.created',
    handleCommercialOperationCreated,
  );
  registerHandler('logistics.scheduled', handleLogisticsScheduled);
  registerHandler('maintenance.scheduled', handleMaintenanceScheduled);
  registerHandler('maintenance.started', handleMaintenanceStarted);
  registerHandler('maintenance.completed', handleMaintenanceCompleted);
  registerHandler('maintenance.conflict', handleMaintenanceConflict);
  registerHandler('inventory.transfer', handleInventoryTransfer);
  registerHandler('inventory.adjustment', handleInventoryAdjustment);
  registerHandler('inventory.discrepancy', handleInventoryDiscrepancy);
  registerHandler('inventory.movement.critical', handleInventoryMovementCritical);
  registerHandler('invoice.created', handleInvoiceCreated);
  registerHandler('invoice.pending', handleInvoicePending);
  registerHandler('user.permission_denied', handlePermissionDenied);
}
